"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface LayoutGridIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface LayoutGridIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const LayoutGridIcon = forwardRef<LayoutGridIconHandle, LayoutGridIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const gridVariants: Variants = {
      normal: { scale: 1, rotate: 0 },
      animate: {
        scale: [1, 1.03, 1],
        rotate: [0, 1, 0],
        transition: {
          duration: 0.6 * duration,
          ease: "easeInOut" as const,
        },
      },
    };

    const tileVariants: Variants = {
      normal: { opacity: 1, scale: 1 },
      animate: (i: number) => ({
        opacity: [0.4, 1],
        scale: [0.85, 1.08, 1],
        transition: {
          duration: 0.55 * duration,
          delay: 0.08 * i,
          ease: "easeOut" as const,
        },
      }),
    };

    const sweepVariants: Variants = {
      normal: { x: -26, y: -26, opacity: 0 },
      animate: {
        x: [-26, 26],
        y: [-26, 26],
        opacity: [0, 0.35, 0],
        transition: {
          duration: 0.8 * duration,
          ease: "easeInOut" as const,
          delay: 0.1,
        },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
      >
        <defs>
          <linearGradient id="grid-sweep" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
            <stop offset="50%" stopColor="currentColor" stopOpacity="0.35" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>

        <motion.g variants={gridVariants} initial="normal" animate={controls}>
          <motion.rect
            width="7"
            height="7"
            x="3"
            y="3"
            rx="1"
            variants={tileVariants}
            custom={0}
            initial="normal"
            animate={controls}
          />
          <motion.rect
            width="7"
            height="7"
            x="14"
            y="3"
            rx="1"
            variants={tileVariants}
            custom={1}
            initial="normal"
            animate={controls}
          />
          <motion.rect
            width="7"
            height="7"
            x="14"
            y="14"
            rx="1"
            variants={tileVariants}
            custom={2}
            initial="normal"
            animate={controls}
          />
          <motion.rect
            width="7"
            height="7"
            x="3"
            y="14"
            rx="1"
            variants={tileVariants}
            custom={3}
            initial="normal"
            animate={controls}
          />

          <motion.rect
            x="2"
            y="2"
            width="20"
            height="20"
            rx="3"
            fill="url(#grid-sweep)"
            variants={sweepVariants}
            initial="normal"
            animate={controls}
            style={{ pointerEvents: "none" }}
          />
        </motion.g>
      </motion.svg>
    );
  },
);

LayoutGridIcon.displayName = "LayoutGridIcon";
export { LayoutGridIcon };
